/**
 * screens/Settings/components/FeatureConfigCard
 * ──────────────────────────────────────────────────────────────────────────
 * The four just-in-time settings, each with a Configured / Not set chip and the
 * one action that resolves it.
 *
 * These are the SAME four gates the screens report at point of use. This card
 * exists so an admin can complete them deliberately rather than only when a
 * screen blocks them — but the product never asks for all four up front, which
 * is why onboarding is two questions and not six.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { StyleSheet, Text, View } from 'react-native';
import { Button, StatusPill } from '../../../components';
import { neutral } from '../../../theme/colors';
import { sans, size as fontSize, leading } from '../../../theme/typography';
import type { Policy } from '../../../schemas/config';
import SettingsCard from './SettingsCard';
import SettingsRow from './SettingsRow';

export interface FeatureConfigCardProps {
  policy: Policy | null;
  categoryCount: number;
  /** Sets `variance_set` — shortfall to Payroll, surplus to Billing. */
  onSetVariance: () => void;
  /** Connects both Payroll and Billing. Either alone leaves half the queue stuck. */
  onConnect: () => void;
  onEnableOffboardFeed: () => void;
  onAddCategories: () => void;
  busy?: boolean;
}

export function FeatureConfigCard({
  policy,
  categoryCount,
  onSetVariance,
  onConnect,
  onEnableOffboardFeed,
  onAddCategories,
  busy = false,
}: FeatureConfigCardProps) {
  const connectorsReady =
    !!policy && ['payroll', 'billing'].every((c) => policy.connectors.includes(c));

  const features = [
    {
      key: 'categories',
      label: 'Spend categories',
      hint: 'Asked before the first spend. Every spend must carry one.',
      done: categoryCount > 0,
      cta: 'Add categories',
      onPress: onAddCategories,
    },
    {
      key: 'variance',
      label: 'Variance routing',
      hint: 'Asked before the first cash count. A shortfall goes to Payroll, a surplus to Billing.',
      done: !!policy?.variance_set,
      cta: 'Set routing',
      onPress: onSetVariance,
    },
    {
      key: 'connectors',
      label: 'Payroll and Billing',
      hint: 'Asked before anything syncs out. Both are needed, not either.',
      done: connectorsReady,
      cta: 'Connect both',
      onPress: onConnect,
    },
    {
      key: 'offboard',
      label: 'Offboarding feed',
      hint: 'Asked when someone leaves. Without it this console cannot know who is leaving.',
      done: !!policy?.offboard_feed,
      cta: 'Connect feed',
      onPress: onEnableOffboardFeed,
    },
  ];

  return (
    <SettingsCard
      title="Feature configuration"
      subtitle="Asked when you first open the screen that needs it."
    >
      {features.map((feature, index) => (
        <SettingsRow key={feature.key} divided={index < features.length - 1}>
          <View style={styles.row}>
            <View style={styles.text}>
              <Text style={styles.label}>{feature.label}</Text>
              <Text style={styles.hint}>{feature.hint}</Text>
            </View>
            <StatusPill
              label={feature.done ? 'Configured' : 'Not set'}
              tone={feature.done ? 'positive' : 'pending'}
              capitalize={false}
            />
            {!feature.done && (
              <Button
                label={feature.cta}
                size="sm"
                variant="primary"
                loading={busy}
                onPress={feature.onPress}
              />
            )}
          </View>
        </SettingsRow>
      ))}
    </SettingsCard>
  );
}

const styles = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  text: { flex: 1, minWidth: 200, gap: 2 },
  label: { ...sans('500'), fontSize: fontSize.button, color: neutral.ink },
  hint: {
    ...sans('400'),
    fontSize: fontSize.meta,
    lineHeight: leading(fontSize.meta),
    color: neutral.grey600,
  },
});

export default FeatureConfigCard;
